import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  ChartEvent,
  ActiveElement,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import {
  format,
  isSameYear,
  startOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isWithinInterval,
  differenceInCalendarDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { BlockCounted } from '../blockCounted';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';
import { formatAmount, formatCurrency } from '../../utils/formatMoney';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export interface OperationData {
  id: number;
  amount: number;
  name: string;
  date: string;
}

interface ChartBarProps {
  period: PeriodType;
  dataType: 'income' | 'expense' | 'all';
  from?: string;
  to?: string;
}

type BuiltData = { labels: string[]; incomeData: number[]; expenseData: number[] };

export const ChartBar: React.FC<ChartBarProps> = ({ period, dataType, from, to }) => {
  const queryParams = useMemo(() => {
    if (period === 'custom') {
      return {
        from: from || undefined,
        to: to || undefined,
      };
    }

    return undefined;
  }, [period, from, to]);

  const { data: incomes = [] } = useQuery<OperationData[]>({
    queryKey: ['incomes', period, from, to],
    queryFn: () =>
      api
        .get('/incomes', {
          params: queryParams,
        })
        .then(r => r.data),
  });

  const { data: expenses = [] } = useQuery<OperationData[]>({
    queryKey: ['expenses', period, from, to],
    queryFn: () =>
      api
        .get('/expenses', {
          params: queryParams,
        })
        .then(r => r.data),
  });

  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);

  const { labels, incomeData, expenseData }: BuiltData = useMemo(() => {
    const lbls: string[] = [];
    const inc: number[] = [];
    const exp: number[] = [];

    if (period === 'years') {
      const yearsSet = new Set<number>();
      incomes.forEach(o => yearsSet.add(new Date(o.date).getFullYear()));
      expenses.forEach(o => yearsSet.add(new Date(o.date).getFullYear()));

      const years = Array.from(yearsSet).sort((a, b) => a - b);

      years.forEach((y, i) => {
        lbls.push(String(y));
        inc[i] = 0;
        exp[i] = 0;
      });

      incomes.forEach(o => {
        const y = new Date(o.date).getFullYear();
        const idx = years.indexOf(y);
        if (idx >= 0) inc[idx] += Number(o.amount);
      });

      expenses.forEach(o => {
        const y = new Date(o.date).getFullYear();
        const idx = years.indexOf(y);
        if (idx >= 0) exp[idx] += Number(o.amount);
      });

      return { labels: lbls, incomeData: inc, expenseData: exp };
    }

    if (period === 'year') {
      const y = now.getFullYear();
      const months = Array.from({ length: 12 }, (_, i) => i).reverse();

      months.forEach((m, idx) => {
        lbls.push(format(new Date(y, m, 1), 'LLLL', { locale: ru }));
        inc[idx] = 0;
        exp[idx] = 0;
      });

      const monthToIndex = (m: number) => months.indexOf(m);

      incomes.forEach(o => {
        const d = new Date(o.date);
        if (!isSameYear(d, now)) return;
        const idx = monthToIndex(d.getMonth());
        if (idx >= 0) inc[idx] += Number(o.amount);
      });

      expenses.forEach(o => {
        const d = new Date(o.date);
        if (!isSameYear(d, now)) return;
        const idx = monthToIndex(d.getMonth());
        if (idx >= 0) exp[idx] += Number(o.amount);
      });

      return { labels: lbls, incomeData: inc, expenseData: exp };
    }

    if (period === 'month') {
      const y = now.getFullYear();
      const m = now.getMonth();
      const daysPassed = now.getDate();
      const weeksCount = Math.ceil(daysPassed / 7);
      const weekIndexes = Array.from({ length: weeksCount }, (_, i) => i).reverse();

      weekIndexes.forEach((w, idx) => {
        const start = w * 7 + 1;
        const end = Math.min((w + 1) * 7, daysPassed);
        lbls.push(`${start}-${end} ${format(now, 'LLLL', { locale: ru })}`);
        inc[idx] = 0;
        exp[idx] = 0;
      });

      const weekToIndex = (weekIdx: number) => weekIndexes.indexOf(weekIdx);

      incomes.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== y || d.getMonth() !== m) return;
        if (d.getDate() > daysPassed) return;
        const w = Math.floor((d.getDate() - 1) / 7);
        const idx = weekToIndex(w);
        if (idx >= 0) inc[idx] += Number(o.amount);
      });

      expenses.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== y || d.getMonth() !== m) return;
        if (d.getDate() > daysPassed) return;
        const w = Math.floor((d.getDate() - 1) / 7);
        const idx = weekToIndex(w);
        if (idx >= 0) exp[idx] += Number(o.amount);
      });

      return { labels: lbls, incomeData: inc, expenseData: exp };
    }

    if (period === 'week') {
      const start = startOfWeek(now, { locale: ru, weekStartsOn: 1 });
      const days = eachDayOfInterval({ start, end: now });
      const daysRev = [...days].reverse();

      daysRev.forEach((d, idx) => {
        lbls.push(format(d, 'EEEEEE', { locale: ru }));
        inc[idx] = 0;
        exp[idx] = 0;
      });

      const dayToIndex = (d: Date) => daysRev.findIndex(x => x.toDateString() === d.toDateString());

      incomes.forEach(o => {
        const d = new Date(o.date);
        const idx = dayToIndex(d);
        if (idx >= 0) inc[idx] += Number(o.amount);
      });

      expenses.forEach(o => {
        const d = new Date(o.date);
        const idx = dayToIndex(d);
        if (idx >= 0) exp[idx] += Number(o.amount);
      });

      return { labels: lbls, incomeData: inc, expenseData: exp };
    }

    if (period === 'custom' && from && to) {
      const start = new Date(from);
      const end = new Date(to);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
        return { labels: [], incomeData: [], expenseData: [] };
      }

      const totalDays = differenceInCalendarDays(end, start) + 1;

      // маленький диапазон — по дням
      if (totalDays <= 31) {
        const days = eachDayOfInterval({ start, end });

        days.forEach((day, idx) => {
          lbls.push(format(day, 'dd.MM', { locale: ru }));
          inc[idx] = 0;
          exp[idx] = 0;
        });

        const dayToIndex = (d: Date) => days.findIndex(x => x.toDateString() === d.toDateString());

        incomes.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;
          const idx = dayToIndex(d);
          if (idx >= 0) inc[idx] += Number(o.amount);
        });

        expenses.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;
          const idx = dayToIndex(d);
          if (idx >= 0) exp[idx] += Number(o.amount);
        });

        return { labels: lbls, incomeData: inc, expenseData: exp };
      }

      // средний диапазон — по неделям
      if (totalDays <= 120) {
        const days = eachDayOfInterval({ start, end });
        const weekMap = new Map<string, number>();

        days.forEach(day => {
          const weekStart = startOfWeek(day, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          if (!weekMap.has(key)) {
            weekMap.set(key, weekMap.size);
            lbls.push(`${format(weekStart, 'dd.MM')} нед.`);
            inc.push(0);
            exp.push(0);
          }
        });

        incomes.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;
          const weekStart = startOfWeek(d, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);
          if (idx !== undefined) inc[idx] += Number(o.amount);
        });

        expenses.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;
          const weekStart = startOfWeek(d, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);
          if (idx !== undefined) exp[idx] += Number(o.amount);
        });

        return { labels: lbls, incomeData: inc, expenseData: exp };
      }

      const months = eachMonthOfInterval({ start, end });

      months.forEach((month, idx) => {
        lbls.push(format(month, 'LLLL yyyy', { locale: ru }));
        inc[idx] = 0;
        exp[idx] = 0;
      });

      const monthToIndex = (d: Date) =>
        months.findIndex(
          month => month.getFullYear() === d.getFullYear() && month.getMonth() === d.getMonth(),
        );

      incomes.forEach(o => {
        const d = new Date(o.date);
        if (!isWithinInterval(d, { start, end })) return;
        const idx = monthToIndex(d);
        if (idx >= 0) inc[idx] += Number(o.amount);
      });

      expenses.forEach(o => {
        const d = new Date(o.date);
        if (!isWithinInterval(d, { start, end })) return;
        const idx = monthToIndex(d);
        if (idx >= 0) exp[idx] += Number(o.amount);
      });

      return { labels: lbls, incomeData: inc, expenseData: exp };
    }

    return { labels: [], incomeData: [], expenseData: [] };
  }, [period, now, incomes, expenses, from, to]);

  const totalIncome = useMemo(() => incomeData.reduce((a, b) => a + b, 0), [incomeData]);

  const totalExpense = useMemo(() => expenseData.reduce((a, b) => a + b, 0), [expenseData]);

  const totalAll = totalIncome + totalExpense;

  const incomePercent = totalAll > 0 ? Math.round((totalIncome / totalAll) * 100) : 0;
  const expensePercent = totalAll > 0 ? Math.round((totalExpense / totalAll) * 100) : 0;

  const datasets = useMemo(() => {
    if (dataType === 'income') {
      return [{ label: 'Доходы', data: incomeData, backgroundColor: 'rgba(28,172,0,0.5)' }];
    }

    if (dataType === 'expense') {
      return [{ label: 'Расходы', data: expenseData, backgroundColor: 'rgba(255,0,0,0.5)' }];
    }

    return [
      {
        label: `Доходы (${incomePercent}%)`,
        data: incomeData,
        backgroundColor: 'rgba(28,172,0,0.5)',
      },
      {
        label: `Расходы (${expensePercent}%)`,
        data: expenseData,
        backgroundColor: 'rgba(255,0,0,0.5)',
      },
    ];
  }, [dataType, incomeData, expenseData, incomePercent, expensePercent]);

  const data: ChartData<'bar'> = useMemo(
    () => ({
      labels,
      datasets,
    }),
    [labels, datasets],
  );

  const options: ChartOptions<'bar'> = useMemo(() => {
    const xTitle =
      period === 'years'
        ? 'Год'
        : period === 'year'
          ? 'Месяц'
          : period === 'month'
            ? 'Неделя'
            : period === 'week'
              ? 'День недели'
              : 'Выбранный период';

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 6, right: 6, top: 6, bottom: 6 },
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 12,
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 11 : 13;
              return { size };
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const value = Number(ctx.parsed.y ?? 0);
              const dsIndex = ctx.datasetIndex;
              const sum = dsIndex === 0 ? totalIncome : totalExpense;
              const pct = sum > 0 ? Math.round((value / sum) * 100) : 0;
              const label = ctx.dataset.label ?? '';
              return `${label}: ${formatCurrency(value)} (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: xTitle },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 10 : 12;
              return { size };
            },
          },
        },
        y: {
          title: { display: true, text: 'Сумма, ₽' },
          beginAtZero: true,
          ticks: {
            callback: value => formatAmount(Number(value)),
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 10 : 12;
              return { size };
            },
          },
        },
      },
      ...(dataType === 'all' && {
        onClick: (_e: ChartEvent, elements: ActiveElement[]) => {
          if (!elements.length) return;
          const dsIndex = elements[0].datasetIndex;
          if (dsIndex === 0) navigate('/incomes');
          if (dsIndex === 1) navigate('/expenses');
        },
        onHover: (e: ChartEvent, els: ActiveElement[]) => {
          const target = e.native?.target as HTMLCanvasElement | undefined;
          if (!target) return;
          target.style.cursor = els.length ? 'pointer' : 'default';
        },
      }),
    };
  }, [period, dataType, navigate, totalIncome, totalExpense]);

  const hasData = useMemo(() => {
    const hasIncome = incomeData.some(v => v > 0);
    const hasExpense = expenseData.some(v => v > 0);

    if (dataType === 'income') return hasIncome;
    if (dataType === 'expense') return hasExpense;
    return hasIncome || hasExpense;
  }, [dataType, incomeData, expenseData]);

  return (
    <div>
      <BlockCounted type={dataType} incomeData={incomeData} expenseData={expenseData} />

      {hasData ? (
        <Box
          sx={{
            width: '100%',
            height: { xs: 320, sm: 380, md: 440 },
          }}
        >
          <Bar data={data} options={options} />
        </Box>
      ) : (
        <div>Нет данных</div>
      )}
    </div>
  );
};
